/*
 * Confidential and Proprietary.
 * Do not distribute without 1-800-Flowers.com, Inc. consent.
 * Copyright 1-800-Flowers.com, Inc. 2019. All rights reserved.
 */

import {
    fork, call, takeEvery, put, select,
} from 'redux-saga/effects';

import mbpLogger from 'mbp-logger';
import mbpUtil from 'mbp-api-util';
import { trackEvent } from '../../../../../TagManager/ducks/TagManager/TagManager-Actions';
import memberDucks from '../../../../../Member/ducks';
import { getOrderId, getPassportBundleData, getFlags } from '../../../../../Common/Common-Selectors';
import {
    getPassportItem,
    getCartOrder,
    getRecipientOrderItems,
    getCartCount,
} from '../../Cart-Selectors';
import { enterpriseAddGiftMessage } from '../../../CartItem/GiftMessageForm/GiftMessageForm-Operations';
import { flagOffShowLoadingMessage, updateShowLoadingMessage } from '../../../../../Common/ducks/ShowLoadingMessage/ShowLoadingMessage-Actions';
import { retrieveCart } from '../RetrieveCart/RetrieveCart-Operations';
import { deleteItem, enterpriseDeleteItem } from './DeleteItem-Actions';
import { checkoutRouteHandler } from '../../../Pages/Pages-Operations';
import { getPassportBundleItemCurrentlyInCart, getProductRetailPrice } from '../../../../../App/App-Selectors';
import { passportBundleItemRemovedCart, setProductRetailPrice } from '../../../../../App/App-Actions';
import cartServiceClient from '../../../../../../../apis/cart-service-apis/cartServiceClient';
import { trackCartUpdate } from '../../../../../TagManager/ducks/TagManager/TagManager-Helpers';
import { prepareRetreiveCart } from '../RetrieveCart/PrepareRetreiveCart-Operations';
import { getBaseCode } from '../../../../../../../app/helpers/tracking/product/dataLayerProductHelpers';
import { generateDataLayerProductArrays } from '../../../../../../../app/helpers/tracking/vendors/Tealium/tealiumProductDataLayerHelpers';
import { clearOrderItemErrors } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Actions';
import { processOrderItemError } from '../../../../../Checkout/ducks/Common/ducks/Error/Error-Operations';
import { callHandleCartFailure } from '../../Cart-Actions';
import { getCartAttributeByNameAndReturnValue } from '../../../../../../../app/helpers/page/pageHelpers';

const REACT_APP_INJECTABLE = mbpUtil.getEnv('REACT_APP_INJECTABLE');

const {
    auth: {
        authOperations,
    },
    profile: {
        profileActions: {
            setProfileUserRole,
        },
    },
} = memberDucks;

const { checkJWT } = authOperations.workers;

const getOrderItemById = (recipients = [], cartItemId = null, type = '') => {
    let productDetails = null;

    (recipients || []).forEach((recipient) => {
        (recipient?.cartItems || []).forEach((cartItem) => {
            const attributes = cartItem.attributes || [];
            if (type === 'greeting') { // For CGC and card isle greeting card
                (cartItem?.greetingCards || []).forEach((cardItem) => {
                    if (cardItem?.cartItemId === cartItemId) {
                        productDetails = {
                            name: cardItem?.product?.productName || '',
                            brand: cardItem?.product?.brandCode || '',
                            partNumber: cardItem?.product?.parentProductCode || '', // need to pass Product ID instead sku
                            baseCode: getBaseCode(cardItem?.product?.parentProductCode || ''),
                            currency: 'USD',
                            quantity: cardItem.itemInfo?.quantity || 1,
                            price: cardItem.product?.listPrice,
                            categoryId: getCartAttributeByNameAndReturnValue(attributes, 'categoryId'),
                            categoryName: getCartAttributeByNameAndReturnValue(attributes, 'categoryIdent'),
                            categoryPath: getCartAttributeByNameAndReturnValue(attributes, 'categoryPath'),
                        };
                    }
                });
            } else if (cartItem?.cartItemId === cartItemId) { // for order item
                productDetails = {
                    name: cartItem.product?.parentProductName || '',
                    brand: cartItem.product?.brandCode || '',
                    partNumber: cartItem.product?.parentProductCode || '', // need to pass Product ID instead sku
                    baseCode: getBaseCode(cartItem.product?.parentProductCode || ''),
                    currency: 'USD',
                    quantity: cartItem.itemInfo?.quantity || 1,
                    price: cartItem.itemInfo?.price,
                    categoryId: getCartAttributeByNameAndReturnValue(attributes, 'categoryId'),
                    categoryName: getCartAttributeByNameAndReturnValue(attributes, 'categoryIdent'),
                    categoryPath: getCartAttributeByNameAndReturnValue(attributes, 'categoryPath'),
                };
            } else if (cartItem.addons?.length > 0) { // if the order item belong to te addons
                const addons = cartItem.addons;
                addons.forEach((addon) => {
                    if (addon.cartItemId === cartItemId && addon.product) {
                        productDetails = {
                            name: addon.product.productName || '',
                            brand: addon.product.brandCode || '',
                            partNumber: addon.product.parentProductCode || '', // need to pass Product ID instead sku
                            baseCode: getBaseCode(addon.product.parentProductCode || ''),
                            currency: 'USD',
                            quantity: addon.itemInfo?.quantity || 1,
                            price: addon.itemInfo.price,
                            categoryId: getCartAttributeByNameAndReturnValue(attributes, 'categoryId'),
                            categoryName: getCartAttributeByNameAndReturnValue(attributes, 'categoryIdent'),
                            categoryPath: getCartAttributeByNameAndReturnValue(attributes, 'categoryPath'),
                        };
                    }
                });
            }
        });
    });

    return productDetails;
};

function* workerDeleteItem(action) {
    const cartId = yield select(getOrderId);
    try {
        const {
            orderItemId, isPassport, typeOrderItem,
            history, skuCode, parentSku, typeGreetingCard,
            orderCartItemId, callback,
        } = action.data;
        yield put(updateShowLoadingMessage());

        // clear error
        yield put(clearOrderItemErrors(
            cartId,
            'cart',
            { errType: '', field: 'error' },
        ));

        yield put(clearOrderItemErrors(
            orderItemId,
            'payment',
            { errType: '', field: orderItemId },
        ));

        const jwtToken = yield call(checkJWT);
        const payload = {
            env: {},
            jwtToken,
            orderItemId,
            cartId,
        };
        const cartProducts = yield select(getRecipientOrderItems);
        const productDetails = getOrderItemById(cartProducts, orderItemId);
        const cartCount = yield select(getCartCount);

        yield call(cartServiceClient.removeOrderItem, payload);
        const isPassportItemInCart = yield select(getPassportBundleItemCurrentlyInCart);
        if (isPassportItemInCart) {
            yield put(passportBundleItemRemovedCart(skuCode));
        }

        const bundleData = yield select(getPassportBundleData);
        const passportData = yield select(getPassportItem);

        const parentSkuCart = passportData?.[0]?.cartItems?.[0]?.product?.parentProductCode || null;
        const bundleSkuData = bundleData?.productSku || null;
        if (
            parentSku // Dont delete this if all the elements are undefined is trying delete twice.
            && bundleSkuData // Dont delete this if all the elements are undefined is trying delete twice.
            && parentSkuCart  // Dont delete this if all the elements are undefined is trying delete twice.
            && parentSku === bundleSkuData
            && parentSkuCart === bundleData?.passportSku
        ) {
            const passportOrderId = passportData?.[0]?.cartItems?.[0].cartItemId;
            const passConfigObj = {
                env: {},
                jwtToken,
                orderItemId: passportOrderId,
                cartId,
            };
            yield call(cartServiceClient.removeOrderItem, passConfigObj);
        }

        // To remove retail price which has been removed from cart
        // TODO: remove this code once we will get retail price and sale price form cart api for checkout flow
        const listOfRetailPrices = yield select(getProductRetailPrice);
        if (listOfRetailPrices) {
            const modifiedPriceList = listOfRetailPrices.filter((item) => !(Object.keys(item)).includes(orderItemId));
            yield put(setProductRetailPrice(modifiedPriceList));
        }

        // cardIsle
        if (typeGreetingCard === 'greetingCard') {
            yield call(enterpriseAddGiftMessage, {
                data: {
                    isMultiline: false,
                    giftMessage: {
                        message: 'No Card Message',
                        signature: '',
                    },
                    orderItemId: orderCartItemId,
                },
            });
        }

        const eventDataLayer = generateDataLayerProductArrays({
            products: [
                {
                    name: productDetails?.name || action?.data?.parentSku || '',
                    brand: productDetails?.brand || action?.data?.brandId || '',
                    partNumber: productDetails?.partNumber || action?.data?.parentSku || '', // need to pass Product ID instead sku
                    baseCode: productDetails?.baseCode || getBaseCode(action?.data?.parentSku || ''),
                    currency: productDetails?.currency || 'USD',
                    position: action?.data?.positionItem || 1,
                    quantity: productDetails?.quantity || 1,
                    price: productDetails?.price,
                    categoryId: productDetails?.categoryId || '',
                    categoryName: productDetails?.categoryName || '',
                    categoryPath: productDetails?.categoryPath || '',
                },
            ],
        });
        // Tealium Tracking Start
        const eventAction = 'Remove from cart';
        let removeItemTracker = {};
        if (isPassport) {
            removeItemTracker = {
                eventCategory: 'Checkout',
                eventAction,
                eventLabel: 'Passport',
                ...eventDataLayer,
            };

            yield put(setProfileUserRole(''));
        } else {
            removeItemTracker = {
                eventCategory: 'Checkout',
                eventAction,
                eventLabel: `${skuCode}|<<pageType>>`,
                ...eventDataLayer,
            };
        }
        yield put(trackEvent(removeItemTracker));
        // Tealium Tracking Ends

        // When cartCount is equal to 1, that means the cart is going to be empty after the operations.
        // only call calculatecart when there are more items left in the cart
        if (cartCount > 1) {
            yield call(prepareRetreiveCart, typeOrderItem);
        }
        yield call(retrieveCart);
        yield call(checkoutRouteHandler, history);

        if (callback) {
            // close modal
            yield call(callback, false);
        }

        const flagState = yield select(getFlags);

        // current product is not work in redux anymore we  need fix this.
        // if (typeof window !== 'undefined' && window.location.href.indexOf('/shipping/') > 0) {
        //     const cIEnabled = flagState['is-card-isle-enabled'] || false;

        //     if (cIEnabled && !isPassport) {
        //         // Review the product has card isle available.
        //         yield call(loadCardIsleAvailable);
        //     }
        // }

        yield put(flagOffShowLoadingMessage());

        // Retrive current cart
        // Send cart snapshot to cart track event
        const fdOrder = yield select(getCartOrder);
        trackCartUpdate(flagState, 'EventRemoveFromCart', fdOrder);
    } catch (ex) {
        mbpLogger.logError({
            orderItem: action.data,
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'delete item',
            jsError: ex,
            message: 'delete item Failed',
        });
        yield fork(processOrderItemError, {
            data: {
                orderItemId: action.data.orderItemId,
                errorFeatureType: 'cart',
                errorValues: ex.response?.data,
            },
        });
        // Looking if the cart still have authorization
        yield put(callHandleCartFailure(ex));

        // End loader
        yield put(flagOffShowLoadingMessage());
    }
}

function* watchDeleteItem() {
    yield takeEvery(deleteItem().type, workerDeleteItem);
}

function* workerHandleDeleteItem(action) {
    const cartId = yield select(getOrderId);
    try {
        // Show Loader
        yield put(updateShowLoadingMessage());

        // Prepare Delete
        const {
            orderItemId, isPassport, history, typeOrderItem, skuCode,
            parentSku, type, orderCartItemId,
        } = action.data;
        const page = typeOrderItem;
        const jwtToken = yield call(checkJWT);
        const payload = {
            wcEnv: {},
            jwtToken,
            orderItemId,
            cartId,
        };

        const cartProducts = yield select(getRecipientOrderItems);
        const productDetails = getOrderItemById(cartProducts, orderItemId, type);

        // Call Delete Item API
        yield call(cartServiceClient.removeOrderItem, payload);

        const bundleData = yield select(getPassportBundleData);
        const passportData = yield select(getPassportItem);

        if (
            parentSku // Dont delete this if all the elements are undefined is trying delete twice.
            && bundleData // Dont delete this if all the elements are undefined is trying delete twice.
            && passportData?.[0]?.cartItems?.[0].product?.parentProductCode  // Dont delete this if all the elements are undefined is trying delete twice.
            && parentSku === bundleData?.productSku
            && passportData[0].cartItems[0].product.parentProductCode === bundleData?.passportSku
            && passportData?.[0]?.cartItems?.[0].cartItemId
        ) {
            const passportOrderId = passportData?.[0]?.cartItems?.[0].cartItemId;
            const passConfigObj = {
                env: {},
                jwtToken,
                orderItemId: passportOrderId,
                cartId,
            };
            yield call(cartServiceClient.removeOrderItem, passConfigObj);
        }

        // Clear Error
        yield put(clearOrderItemErrors(
            cartId,
            'cart',
            { errType: '', field: 'error' },
        ));

        if (type === 'greeting') {
            yield call(enterpriseAddGiftMessage, {
                data: {
                    isMultiline: false,
                    giftMessage: {
                        message: 'No Card Message',
                        signature: '',
                    },
                    orderItemId: orderCartItemId,
                },
            });
        }

        const eventDataLayer = generateDataLayerProductArrays({
            products: [
                {
                    name: productDetails?.name || action?.data?.parentSku || '',
                    brand: productDetails?.brand || action?.data?.brandId || '',
                    partNumber: productDetails?.partNumber || action?.data?.parentSku || '', // need to pass Product ID instead sku
                    baseCode: productDetails?.baseCode || getBaseCode(action?.data?.parentSku || ''),
                    currency: productDetails?.currency || 'USD',
                    position: action?.data?.positionItem || 1,
                    quantity: Number(productDetails?.quantity) || 1,
                    price: Number(productDetails?.price),

                    categoryId: productDetails?.categoryId || '',
                    categoryName: productDetails?.categoryName || '',
                    categoryPath: productDetails?.categoryPath || '',
                },
            ],
        });

        // Tealium Tracking Start
        const eventAction = 'Remove from cart';
        let removeItemTracker = {};
        if (isPassport) {
            removeItemTracker = {
                eventCategory: 'Checkout',
                eventAction,
                eventLabel: 'Passport|<<pageType>>',
                ...eventDataLayer,
            };
        } else {
            removeItemTracker = {
                eventCategory: 'Checkout',
                eventAction,
                eventLabel: `${skuCode}|<<pageType>>`,
                ...eventDataLayer,
            };
        }
        yield put(trackEvent(removeItemTracker));
        // Tealium Tracking Ends

        yield call(prepareRetreiveCart, page);

        // Get Latest Cart
        yield call(retrieveCart);

        yield call(checkoutRouteHandler, history);

        // Hide Loader
        yield put(flagOffShowLoadingMessage());

        if (REACT_APP_INJECTABLE === 'false' && typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('refresh-cart-count', { detail: {} }));
        }
    } catch (ex) {
        mbpLogger.logError({
            cartId,
            orderItem: action.data,
            appName: process.env.npm_package_name,
            module: 'mbp-checkout',
            function: 'delete item',
            jsError: ex,
            message: 'delete item Failed',
        });
        yield put(callHandleCartFailure(ex));
        yield fork(processOrderItemError, {
            data: {
                orderItemId: action.data.orderItemId,
                errorFeatureType: 'cart',
                errorValues: ex.response?.data,
            },
        });
        yield put(flagOffShowLoadingMessage());
    }
}

function* watchEnterpriseDeleteItem() {
    yield takeEvery(enterpriseDeleteItem().type, workerHandleDeleteItem);
}

const watchers = [
    fork(watchDeleteItem),
    fork(watchEnterpriseDeleteItem),
];

export { watchers, workerDeleteItem };

export default {};
